import { useViewer } from '../../../context/viewer';
import { trpc } from '../../../../trpc/react';

/**
 * Preloads the org/team-scoped exercise library so the block builder's
 * exercise search can filter locally instead of querying per keystroke.
 * Mounted high in the block builder so the fetch runs in the background;
 * pickers share the same react-query cache entry.
 *
 * Callers pass the block's team where known: on a hard refresh the page
 * corrects `selectedTeam` to the block's team in an effect *after* mount, so
 * keying the preload off `selectedTeam` alone can fetch the wrong team's
 * library first.
 */
export function useBlockExercises(teamId?: string | null) {
  const {
    team: { selectedTeam, teams },
  } = useViewer();

  const requestedTeamId = teamId ?? selectedTeam;
  // Never request a team the viewer can't access. A read-only cross-team
  // block passes the block's own team, which the viewer isn't a member of:
  // the library route rejects that request, and nothing in that view
  // consumes the preload (the picker only opens when editing).
  const canLoad =
    !requestedTeamId || teams.some((team) => team.id === requestedTeamId);

  return trpc.exerciseLibrary.blockBuilderExercises.useQuery(
    { teamId: requestedTeamId || undefined },
    // The library rarely changes mid-session; the create flow updates the
    // cache entry directly when it adds an exercise. The whole library is a
    // large payload, so don't re-download it on every window focus.
    {
      enabled: canLoad,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}
